/**
 * An audio processor node from one of our public/ worklet scripts — an AudioWorkletNode wherever the
 * platform has AudioWorklet, and otherwise THE SAME PROCESSOR CODE run inside a ScriptProcessorNode.
 *
 * Why not a second implementation for old engines: native-pcm-worklet.js is the jitter buffer every
 * receiver's sound depends on (priming, drift steering, concealment, playout clock). A parallel
 * ScriptProcessor version would drift from it the first time either is tuned. Instead the fallback
 * evaluates the worklet's own source with the AudioWorkletGlobalScope pieces it uses — the
 * AudioWorkletProcessor base (its `port`), registerProcessor, sampleRate, currentFrame/currentTime —
 * and drives process() in 128-frame render quanta, exactly as the audio thread would. Callers see one
 * shape: an AudioNode with a `port`.
 *
 * Its clock IS the context's: `currentFrame` / `currentTime` of each quantum are stamped from the callback's
 * `playbackTime` (the context time its output buffer plays at) plus the quantum's offset in it — never a count of
 * the callbacks this page happened to run. A processor that reports `currentFrame` (native-pcm-worklet's playout
 * clock, which the page extrapolates on ctx.currentTime — cast/session.ts) is then on the same timeline as the
 * page reads it against, however late the processor was created or however many callbacks a stalled page lost.
 *
 * The fallback runs on the MAIN thread, so a janky render frame can starve it (that is why the worklet
 * exists). It is for engines without AudioWorklet, not a peer: selection is pure feature detection.
 */
import { scriptSource, scriptUrl } from './publicUrl'

export type PortNode = AudioNode & { readonly port: MessagePort }

export interface ProcessorSpec {
  /** the public/ script, e.g. '/native-pcm-worklet.js' */
  script: string
  /** the name it registerProcessor()s */
  name: string
  numberOfInputs: number
  outputChannels: number
  processorOptions?: Record<string, unknown>
}

/** Does this engine run AudioWorklet? (Chrome 66+ — every TV we target; the fallback is insurance.) */
export function hasAudioWorklet(ctx: BaseAudioContext): boolean {
  return typeof AudioWorkletNode === 'function' && !!(ctx as { audioWorklet?: unknown }).audioWorklet
}

export async function createProcessorNode(ctx: AudioContext, spec: ProcessorSpec): Promise<PortNode> {
  if (hasAudioWorklet(ctx)) {
    await ctx.audioWorklet.addModule(await scriptUrl(spec.script))
    return new AudioWorkletNode(ctx, spec.name, {
      numberOfInputs: spec.numberOfInputs,
      numberOfOutputs: 1,
      outputChannelCount: [spec.outputChannels],
      processorOptions: spec.processorOptions
    })
  }
  console.warn(`[syqel] no AudioWorklet — running ${spec.name} in a ScriptProcessor (main thread)`)
  return scriptProcessorNode(ctx, spec, await scriptSource(spec.script))
}

const QUANTUM = 128 // the Web Audio render quantum — process() always sees blocks of this size
const BUFFER = 2048 // ScriptProcessor callback size (~43 ms at 48 kHz): fewer main-thread wakeups
// Chrome garbage-collects a ScriptProcessorNode only JS references (its onaudioprocess stops firing).
const live = new Set<ScriptProcessorNode>()

interface ProcessorScope {
  create(name: string, options: unknown, port: MessagePort): { process(i: Float32Array[][], o: Float32Array[][], p: unknown): boolean }
  at(frame: number): void
}

function scriptProcessorNode(ctx: AudioContext, spec: ProcessorSpec, source: string): PortNode {
  // the AudioWorkletGlobalScope the processor code expects, as an ordinary function scope
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const scope = new Function('sampleRate', `
    let currentFrame = 0, currentTime = 0, __port = null
    const __registry = {}
    class AudioWorkletProcessor { constructor() { this.port = __port } }
    function registerProcessor(name, ctor) { __registry[name] = ctor }
    ${source}
    return {
      create(name, options, port) { __port = port; try { return new __registry[name](options) } finally { __port = null } },
      at(frame) { currentFrame = frame; currentTime = frame / sampleRate }
    }`)(ctx.sampleRate) as ProcessorScope

  const channel = new MessageChannel()
  const proc = scope.create(spec.name, { processorOptions: spec.processorOptions ?? {} }, channel.port1)
  // one input channel even for a source node: Chrome rejects a processor with zero on both sides, and an
  // unconnected input simply reads silence
  const inChannels = spec.numberOfInputs > 0 ? 2 : 1
  const node = ctx.createScriptProcessor(BUFFER, inChannels, spec.outputChannels)
  const outs = Array.from({ length: spec.outputChannels }, () => new Float32Array(QUANTUM))
  node.onaudioprocess = (e) => {
    // the context frame this buffer's first sample plays at (playbackTime: Web Audio 1.0 — every engine with a
    // ScriptProcessor has it; the context's current time is its own timeline too, the closest stand-in)
    const at0 = Math.round((Number.isFinite(e.playbackTime) ? e.playbackTime : ctx.currentTime) * ctx.sampleRate)
    const ins: Float32Array[] = []
    if (spec.numberOfInputs > 0) for (let c = 0; c < e.inputBuffer.numberOfChannels; c += 1) ins.push(e.inputBuffer.getChannelData(c))
    const dst = Array.from({ length: spec.outputChannels }, (_, c) => e.outputBuffer.getChannelData(c))
    for (let off = 0; off < BUFFER; off += QUANTUM) {
      scope.at(at0 + off)
      const inputs = spec.numberOfInputs > 0 ? [ins.map((ch) => ch.subarray(off, off + QUANTUM))] : []
      proc.process(inputs, [outs], {})
      for (let c = 0; c < outs.length; c += 1) dst[c].set(outs[c], off)
    }
  }
  live.add(node)
  ctx.addEventListener('statechange', () => { if (ctx.state === 'closed') { live.delete(node); channel.port1.close(); channel.port2.close() } })
  return Object.assign(node, { port: channel.port2 }) as PortNode
}
